import moment from 'moment';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'reactstrap';
import { Drawer } from '../../../core/components/Drawer';
import { apiError } from '../../../core/toastMessages';
import {
  CreateTaskTemplateRequest,
  LinkedTaskTemplateInput,
  TaskTemplate,
  TaskTemplateDependency,
  taskTemplatesApi,
  useArchiveTaskTemplateMediaForGroupMutation,
  useArchiveTaskTemplateMediaMutation,
  useCreateGroupTaskTemplateMutation,
  useCreateTaskTemplateMutation,
  useDeleteGroupTaskTemplateMutation,
  useDeleteTaskTemplateMutation,
  useGetTaskTemplateMediaForGroupQuery,
  useGetTaskTemplateMediaQuery,
  useGetTaskTemplateQuery,
  useUpdateGroupTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import { postTaskTemplateMedia, postTaskTemplateMediaForGroup } from '../../../core/api/files';
import { Files } from '../../../core/components/Files';
import { selectSite, selectRole, selectUserGroup } from '../../../store/authReducer';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import {
  findPermissionRef,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { selectSiteSettingsSite } from '../../settings/reducer';
import LinkedTaskTemplateField from './LinkedTaskTemplateField';
import {
  UserGroupPermissions,
  addPermissionRefs,
  editPermissionRefs,
  deletePermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { selectEmployees } from '../../employees/reducer';
import { selectTaskLocations, selectTaskPrefixes } from '../../tasks/reducer';
import { selectSiteOperationTypes } from '../../settings/reducer';

const TASK_DRAWER_WIDTH = 60;

export const INTERVAL_OPTIONS = [
  'Hourly',
  'Daily',
  'Weekly',
  'Monthly',
  'Yearly',
  'CarCount',
  'Once',
];
export const TYPE_OPTIONS = [
  'Maintenance',
  'OpenChecklist',
  'ClosingChecklist',
  'PreventativeMaintenance',
  'CleaningList',
  'HourlyInspection',
  'ExpressInterior',
];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'] as const;
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export interface DrawerFormState {
  id?: number;
  name: string;
  subTitle: string;
  description: string;
  type: string;
  interval: string;
  intervalCount: number;
  start: string;
  end: string;
  weeklySchedule: string;
  monthlySameWeekday: boolean;
  hourlySchedule: number[];
  intervalCarCount: number | '';
  startingCarCount: number | '';
  skipCompletionsNumber: number | '';
  priority: string;
  employeeId: number | '';
  locationId: number | '';
  operationTypeId: number | '';
  prefixId: number | '';
  alertIfOverdue: boolean;
  estimatedTime: string;
  capacity: number | '';
  resource: string;
  videos: string;
  linkedTaskTemplates: LinkedTaskTemplateInput[];
  applyToAllSitesInGroup: boolean;
}

export const emptyForm = (): DrawerFormState => ({
  name: '',
  subTitle: '',
  description: '',
  type: TYPE_OPTIONS[0],
  interval: INTERVAL_OPTIONS[1],
  intervalCount: 1,
  start: moment().format('YYYY-MM-DDTHH:mm'),
  end: '',
  weeklySchedule: '',
  monthlySameWeekday: false,
  hourlySchedule: [],
  intervalCarCount: '',
  startingCarCount: '',
  skipCompletionsNumber: '',
  priority: 'Low',
  employeeId: '',
  locationId: '',
  operationTypeId: '',
  prefixId: '',
  alertIfOverdue: false,
  estimatedTime: '',
  capacity: '',
  resource: '',
  videos: '',
  linkedTaskTemplates: [],
  applyToAllSitesInGroup: false,
});

export const formFromTask = (task: TaskTemplate): DrawerFormState => ({
  id: task.id,
  name: task.name || '',
  subTitle: task.subTitle || '',
  description: task.description || '',
  type: task.type || TYPE_OPTIONS[0],
  interval: task.interval || INTERVAL_OPTIONS[1],
  intervalCount: task.intervalCount ?? 1,
  start: task.start ? moment(task.start).format('YYYY-MM-DDTHH:mm') : '',
  end: task.end ? moment(task.end).format('YYYY-MM-DDTHH:mm') : '',
  weeklySchedule: task.weeklySchedule || '',
  monthlySameWeekday: Boolean(task.monthlySameWeekday),
  hourlySchedule: Array.isArray(task.hourlySchedule) ? task.hourlySchedule : [],
  intervalCarCount: task.intervalCarCount ?? '',
  startingCarCount: task.startingCarCount ?? '',
  skipCompletionsNumber: task.skipCompletionsNumber ?? '',
  priority: task.priority || 'Low',
  employeeId: task.employeeId ?? '',
  locationId: task.locationId ?? '',
  operationTypeId: task.operationTypeId ?? '',
  prefixId: task.prefixId ?? '',
  alertIfOverdue: Boolean(task.alertIfOverdue),
  estimatedTime: task.estimatedTime ?? '',
  capacity: task.capacity ?? '',
  resource: task.resource || '',
  videos: Array.isArray(task.videos) ? task.videos.join('\n') : '',
  linkedTaskTemplates: (task.linkedTaskTemplates ?? [])
    .filter((link) => !link.archivedAt)
    .map((link) => ({
      id: link.linkedTaskId,
      dependency: link.dependency as TaskTemplateDependency,
    })),
  applyToAllSitesInGroup: task.siteId == null && task.groupId != null,
});

const parseVideos = (raw: string): string[] | null => {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : null;
};

const toggleWeekday = (schedule: string, day: number): string => {
  const present = schedule.includes(String(day));
  const next = present
    ? schedule.replace(String(day), '')
    : `${schedule}${day}`.split('').sort().join('');
  return next;
};

const toggleHour = (schedule: number[], hour: number): number[] => {
  if (schedule.includes(hour)) return schedule.filter((h) => h !== hour);
  return [...schedule, hour].sort((a, b) => a - b);
};

export interface AssetScope {
  assetGroupId?: number | null;
  assetItemId?: number | null;
  assetTemplateId?: number | null;
}

export interface TaskTemplateAdminDrawerProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | null;
  initialTask?: TaskTemplate | null;
  addDefaults?: Partial<DrawerFormState>;
  assetScope?: AssetScope;
  titleOverride?: string;
  onClose: () => void;
  onSaved?: (task: TaskTemplate | null) => void;
  onArchived?: (id: number) => void;
  onOptimisticPatch?: (id: number, patch: Partial<TaskTemplate>) => (() => void) | void;
}

const TaskTemplateAdminDrawer: React.FC<TaskTemplateAdminDrawerProps> = ({
  isOpen,
  mode,
  initialTask,
  addDefaults,
  assetScope,
  titleOverride,
  onClose,
  onSaved,
  onArchived,
  onOptimisticPatch,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees);
  const locations = useSelector(selectTaskLocations) as any[];
  const operationTypes = useSelector(selectSiteOperationTypes) as any[];
  const taskPrefixes = useSelector(selectTaskPrefixes) as Array<{
    type: number;
    message: string;
  }>;
  const siteSettingsSite = useSelector(selectSiteSettingsSite) as {
    groupId?: number | null;
  } | null;
  const groupId: number | null = siteSettingsSite?.groupId ?? null;
  const selectedSiteId = useSelector(selectSite) as number | null;
  const role = useSelector(selectRole);
  const userGroup = useSelector(selectUserGroup);
  const enhancedRole = findPermissionRef(
    (userGroup as any)?.permissions ?? [],
    viewPermissionRefs.admin
  )
    ? 'Admin'
    : role;

  const [drawerForm, setDrawerForm] = useState<DrawerFormState | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  type AdminTab = 'Details' | 'Defaults' | 'Media' | 'Linked Tasks' | 'Item';
  const [activeAdminTab, setActiveAdminTab] = useState<AdminTab>('Details');
  const adminTabs: AdminTab[] = ['Details', 'Defaults', 'Media', 'Linked Tasks', 'Item'];
  const tabStyle = (tab: AdminTab): CSSProperties => ({
    display: activeAdminTab === tab ? 'block' : 'none',
  });

  const seededKeyRef = useRef<string | null>(null);
  const hydratedLinkedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen || !mode) {
      seededKeyRef.current = null;
      hydratedLinkedIdRef.current = null;
      return;
    }
    const key = mode === 'edit' ? `edit:${initialTask?.id ?? 'none'}` : 'add';
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;
    hydratedLinkedIdRef.current = null;
    if (mode === 'edit' && initialTask) {
      setDrawerForm(formFromTask(initialTask));
    } else {
      setDrawerForm({ ...emptyForm(), ...addDefaults });
    }
    setDrawerError(null);
    setActiveAdminTab('Details');
  }, [isOpen, mode, initialTask, addDefaults]);

  const [createTask, { isLoading: isCreating }] = useCreateTaskTemplateMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskTemplateMutation();
  const [deleteTask] = useDeleteTaskTemplateMutation();
  const [createGroupTask, { isLoading: isCreatingGroup }] = useCreateGroupTaskTemplateMutation();
  const [updateGroupTask, { isLoading: isUpdatingGroup }] = useUpdateGroupTaskTemplateMutation();
  const [deleteGroupTask] = useDeleteGroupTaskTemplateMutation();
  const [archiveTaskTemplateMedia] = useArchiveTaskTemplateMediaMutation();
  const [archiveTaskTemplateMediaForGroup] = useArchiveTaskTemplateMediaForGroupMutation();

  const editingTaskId = mode === 'edit' && drawerForm?.id ? drawerForm.id : undefined;
  const { data: editingTaskFull } = useGetTaskTemplateQuery(editingTaskId as number, {
    skip: !editingTaskId,
  });
  const useGroupMediaRoute =
    !!drawerForm?.applyToAllSitesInGroup && groupId != null && editingTaskId != null;
  const { data: siteTaskTemplateMedia = [] } = useGetTaskTemplateMediaQuery(
    editingTaskId as number,
    { skip: !editingTaskId || useGroupMediaRoute }
  );
  const { data: groupTaskTemplateMedia = [] } = useGetTaskTemplateMediaForGroupQuery(
    { groupId: groupId as number, taskTemplateId: editingTaskId as number },
    { skip: !editingTaskId || !useGroupMediaRoute }
  );
  const templateMedia = useGroupMediaRoute ? groupTaskTemplateMedia : siteTaskTemplateMedia;
  const requiredMediaRole = useGroupMediaRoute
    ? RoleTypes.OperatorGroupAdmin
    : RoleTypes.SiteManager;
  const canModifyMedia =
    hasRole(enhancedRole, requiredMediaRole) &&
    (drawerForm?.applyToAllSitesInGroup ? groupId != null : !!selectedSiteId);

  interface PendingMediaItem {
    id: string;
    formData: FormData;
    file: any;
  }
  const [pendingMedia, setPendingMedia] = useState<PendingMediaItem[]>([]);
  const pendingMediaRef = useRef<PendingMediaItem[]>([]);
  useEffect(() => {
    pendingMediaRef.current = pendingMedia;
  }, [pendingMedia]);
  const revokeAllPendingUrls = (items: PendingMediaItem[]): void => {
    items.forEach((item) => {
      if (item.file.downloadUrl) {
        try {
          URL.revokeObjectURL(item.file.downloadUrl);
        } catch {}
      }
    });
  };

  const handleAddTemplateMedia = async (formData: FormData): Promise<void> => {
    if (mode === 'add' || !editingTaskId) {
      const fileBlob = formData.get('file') as File | null;
      if (!fileBlob) return;
      const description = (formData.get('description') as string) || '';
      const previewUrl = URL.createObjectURL(fileBlob);
      const synthetic = {
        id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        originalName: (fileBlob as any).name || 'upload',
        mimeType: fileBlob.type || 'application/octet-stream',
        size: fileBlob.size,
        description,
        downloadUrl: previewUrl,
      };
      setPendingMedia((prev) => [...prev, { id: synthetic.id, formData, file: synthetic }]);
      return;
    }
    try {
      if (useGroupMediaRoute) {
        await postTaskTemplateMediaForGroup({
          options: { groupId: groupId as number },
          data: { taskTemplateId: editingTaskId, file: formData },
        });
      } else {
        if (!selectedSiteId) return;
        await postTaskTemplateMedia({
          options: { siteId: selectedSiteId },
          data: { taskTemplateId: editingTaskId, file: formData },
        });
      }
      dispatch(
        taskTemplatesApi.util.invalidateTags([
          { type: 'TaskTemplate' as const, id: `task-template-media-${editingTaskId}` },
        ])
      );
    } catch (e: any) {
      apiError(e?.data || e?.message || 'Failed to upload media');
    }
  };

  const handleDeleteTemplateMedia = async (file: any): Promise<void> => {
    if (typeof file?.id === 'string' && file.id.startsWith('pending-')) {
      setPendingMedia((prev) => {
        const next = prev.filter((item) => item.id !== file.id);
        const removed = prev.find((item) => item.id === file.id);
        if (removed?.file.downloadUrl) {
          try {
            URL.revokeObjectURL(removed.file.downloadUrl);
          } catch {}
        }
        return next;
      });
      return;
    }
    if (!editingTaskId) return;
    try {
      if (useGroupMediaRoute) {
        await archiveTaskTemplateMediaForGroup({
          groupId: groupId as number,
          taskTemplateId: editingTaskId,
          fileId: file.id,
        }).unwrap();
      } else {
        await archiveTaskTemplateMedia({
          taskTemplateId: editingTaskId,
          fileId: file.id,
        }).unwrap();
      }
    } catch (e: any) {
      apiError(e?.data || 'Failed to remove media');
    }
  };
  const displayedMedia = mode === 'add' ? pendingMedia.map((p) => p.file) : templateMedia;

  useEffect(() => {
    if (mode !== 'edit') return;
    if (editingTaskFull?.id == null) return;
    if (hydratedLinkedIdRef.current === editingTaskFull.id) return;
    hydratedLinkedIdRef.current = editingTaskFull.id;
    setDrawerForm((s) => (s && s.id === editingTaskFull.id ? formFromTask(editingTaskFull) : s));
  }, [mode, editingTaskFull]);

  const closeDrawerInternal = useMemo(
    () => () => {
      revokeAllPendingUrls(pendingMediaRef.current);
      setPendingMedia([]);
      setDrawerForm(null);
      setDrawerError(null);
      onClose();
    },
    [onClose]
  );

  const buildPayload = (form: DrawerFormState): CreateTaskTemplateRequest => {
    const validLinks = form.linkedTaskTemplates.filter((link) => link.id > 0);
    const hadExistingLinks = (editingTaskFull?.linkedTaskTemplates ?? []).some(
      (link) => !link.archivedAt
    );
    const sendLinks = validLinks.length > 0 || hadExistingLinks;
    const payload: any = {
      name: form.name.trim(),
      subTitle: form.subTitle || null,
      description: form.description || null,
      type: form.type,
      interval: form.interval,
      intervalCount: form.intervalCount || 1,
      start: form.start ? moment(form.start).toISOString() : moment().toISOString(),
      end: form.end ? moment(form.end).toISOString() : null,
      weeklySchedule: form.interval === 'Weekly' ? form.weeklySchedule || null : null,
      monthlySameWeekday: form.interval === 'Monthly' ? form.monthlySameWeekday : false,
      hourlySchedule: form.interval === 'Hourly' ? form.hourlySchedule : [],
      intervalCarCount:
        form.interval === 'CarCount' && form.intervalCarCount !== ''
          ? Number(form.intervalCarCount)
          : null,
      startingCarCount:
        form.interval === 'CarCount' && form.startingCarCount !== ''
          ? Number(form.startingCarCount)
          : null,
      skipCompletionsNumber:
        form.skipCompletionsNumber === '' ? null : Number(form.skipCompletionsNumber),
      priority: form.priority,
      employeeId: form.employeeId === '' ? null : Number(form.employeeId),
      locationId: form.locationId === '' ? null : Number(form.locationId),
      operationTypeId: form.operationTypeId === '' ? null : Number(form.operationTypeId),
      prefixId: form.type === 'Project' && form.prefixId !== '' ? Number(form.prefixId) : null,
      alertIfOverdue: form.alertIfOverdue,
      estimatedTime: form.estimatedTime || null,
      capacity: form.capacity === '' ? null : Number(form.capacity),
      resource: form.resource || null,
      videos: parseVideos(form.videos),
      ...(sendLinks ? { linkedTaskTemplates: validLinks } : {}),
    };
    if (mode === 'add' && assetScope) {
      if (assetScope.assetGroupId != null) payload.assetGroupId = assetScope.assetGroupId;
      if (assetScope.assetItemId != null) payload.assetItemId = assetScope.assetItemId;
      if (assetScope.assetTemplateId != null) payload.assetTemplateId = assetScope.assetTemplateId;
    }
    return payload as CreateTaskTemplateRequest;
  };

  const isSaving = isCreating || isUpdating || isCreatingGroup || isUpdatingGroup;

  const handleSaveDrawer = async () => {
    if (!drawerForm) return;
    setDrawerError(null);
    if (!drawerForm.name.trim()) {
      setDrawerError('Name is required');
      return;
    }
    const body = buildPayload(drawerForm);
    const editingId = drawerForm.id;
    let rollback: (() => void) | undefined;
    if (mode === 'edit' && editingId !== undefined && onOptimisticPatch) {
      const result = onOptimisticPatch(editingId, body as Partial<TaskTemplate>);
      if (typeof result === 'function') rollback = result;
    }
    const useGroupRoute = drawerForm.applyToAllSitesInGroup && groupId != null;
    try {
      let saved: TaskTemplate | null = null;
      if (mode === 'edit' && editingId !== undefined) {
        if (useGroupRoute) {
          saved = (await updateGroupTask({
            groupId: groupId as number,
            id: editingId,
            patch: body,
          }).unwrap()) as TaskTemplate;
        } else {
          saved = (await updateTask({ id: editingId, ...body }).unwrap()) as TaskTemplate;
        }
      } else if (useGroupRoute) {
        saved = (await createGroupTask({
          groupId: groupId as number,
          body,
        }).unwrap()) as TaskTemplate;
      } else {
        saved = (await createTask(body).unwrap()) as TaskTemplate;
      }
      const createdId = mode === 'add' ? saved?.id : undefined;
      if (createdId && pendingMediaRef.current.length > 0) {
        for (const item of pendingMediaRef.current) {
          try {
            if (useGroupRoute) {
              await postTaskTemplateMediaForGroup({
                options: { groupId: groupId as number },
                data: { taskTemplateId: createdId, file: item.formData },
              });
            } else {
              if (!selectedSiteId) continue;
              await postTaskTemplateMedia({
                options: { siteId: selectedSiteId },
                data: { taskTemplateId: createdId, file: item.formData },
              });
            }
            try {
              URL.revokeObjectURL(item.file.downloadUrl);
            } catch {}
          } catch (uploadErr: any) {
            apiError(
              `Failed to upload ${item.file.originalName}: ${
                uploadErr?.data || uploadErr?.message || 'Unknown error'
              }`
            );
          }
        }
        dispatch(
          taskTemplatesApi.util.invalidateTags([
            { type: 'TaskTemplate' as const, id: `task-template-media-${createdId}` },
          ])
        );
        setPendingMedia([]);
      }
      if (onSaved) onSaved(saved);
      closeDrawerInternal();
    } catch (e: any) {
      if (rollback) rollback();
      setDrawerError(e?.data || 'Failed to save task');
    }
  };

  const handleArchive = async () => {
    if (!drawerForm?.id) return;
    setDrawerError(null);
    const id = drawerForm.id;
    let rollback: (() => void) | undefined;
    if (onOptimisticPatch) {
      const result = onOptimisticPatch(id, {
        archivedAt: new Date().toISOString(),
      } as Partial<TaskTemplate>);
      if (typeof result === 'function') rollback = result;
    }
    const useGroupRoute = drawerForm.applyToAllSitesInGroup && groupId != null;
    try {
      if (useGroupRoute) {
        await deleteGroupTask({ groupId: groupId as number, id }).unwrap();
      } else {
        await deleteTask(id).unwrap();
      }
      if (onArchived) onArchived(id);
      closeDrawerInternal();
    } catch (e: any) {
      if (rollback) rollback();
      setDrawerError(e?.data || 'Failed to archive task');
    }
  };

  const sectionHeader = (label: string): JSX.Element => (
    <div
      style={{
        margin: '1.5rem 0 0.75rem 0',
        paddingBottom: '0.25rem',
        borderBottom: '1px solid #e5e7eb',
        fontWeight: 600,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#374151',
      }}
    >
      {label}
    </div>
  );

  return (
    <Drawer
      expanded={isOpen && drawerForm !== null}
      maximized={false}
      expandedWidth={TASK_DRAWER_WIDTH}
      onBackdropClick={closeDrawerInternal}
      overlay
    >
      {isOpen && drawerForm && (
        <div style={{ padding: '1.5rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <h2 style={{ margin: 0 }}>
              {titleOverride ||
                (mode === 'edit'
                  ? intl.formatMessage({ id: 'editTask', defaultMessage: 'Edit task' })
                  : intl.formatMessage({ id: 'addTask' }))}
            </h2>
            <button className="button" onClick={closeDrawerInternal} type="button">
              {intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
            </button>
          </div>

          {drawerError && (
            <div className="alert alert-danger" role="alert">
              {drawerError}
            </div>
          )}

          <ul className="nav nav-tabs restyled underline" style={{ marginBottom: '1rem' }}>
            {adminTabs.map((tab) => (
              <li className="nav-item" key={tab}>
                <button
                  type="button"
                  className={`nav-link${activeAdminTab === tab ? ' active' : ''}`}
                  onClick={() => setActiveAdminTab(tab)}
                  style={{ color: '#292b2c' }}
                >
                  {tab === 'Linked Tasks'
                    ? intl.formatMessage({ id: 'linkedTasks', defaultMessage: 'Linked Tasks' })
                    : intl.formatMessage({ id: tab.toLowerCase(), defaultMessage: tab })}
                </button>
              </li>
            ))}
          </ul>

          <div style={tabStyle('Details')}>
            {sectionHeader(intl.formatMessage({ id: 'details', defaultMessage: 'Details' }))}
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'name' })}</label>
              <input
                className="form-control"
                value={drawerForm.name}
                onChange={(e) => setDrawerForm((s) => (s ? { ...s, name: e.target.value } : s))}
              />
            </div>
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'subTitle', defaultMessage: 'Subtitle' })}</label>
              <input
                className="form-control"
                value={drawerForm.subTitle}
                onChange={(e) => setDrawerForm((s) => (s ? { ...s, subTitle: e.target.value } : s))}
              />
            </div>
            <div className="form-group">
              <label>
                {intl.formatMessage({ id: 'description', defaultMessage: 'Description' })}
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={drawerForm.description}
                onChange={(e) =>
                  setDrawerForm((s) => (s ? { ...s, description: e.target.value } : s))
                }
              />
            </div>
            <Row>
              <Col xs={6}>
                <div className="form-group">
                  <label>{intl.formatMessage({ id: 'taskType' })}</label>
                  <select
                    className="form-control"
                    value={drawerForm.type}
                    onChange={(e) => setDrawerForm((s) => (s ? { ...s, type: e.target.value } : s))}
                  >
                    {TYPE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col xs={6}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({ id: 'priority', defaultMessage: 'Priority' })}
                  </label>
                  <select
                    className="form-control"
                    value={drawerForm.priority}
                    onChange={(e) =>
                      setDrawerForm((s) => (s ? { ...s, priority: e.target.value } : s))
                    }
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            {sectionHeader(intl.formatMessage({ id: 'schedule', defaultMessage: 'Schedule' }))}
            <Row>
              <Col xs={6}>
                <div className="form-group">
                  <label>{intl.formatMessage({ id: 'interval' })}</label>
                  <select
                    className="form-control"
                    value={drawerForm.interval}
                    onChange={(e) =>
                      setDrawerForm((s) => (s ? { ...s, interval: e.target.value } : s))
                    }
                  >
                    {INTERVAL_OPTIONS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col xs={6}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({
                      id: 'intervalCount',
                      defaultMessage: 'Every (count)',
                    })}
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={drawerForm.intervalCount}
                    onChange={(e) =>
                      setDrawerForm((s) =>
                        s ? { ...s, intervalCount: Number(e.target.value) || 1 } : s
                      )
                    }
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <div className="form-group">
                  <label>{intl.formatMessage({ id: 'startDate' })}</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={drawerForm.start}
                    onChange={(e) =>
                      setDrawerForm((s) => (s ? { ...s, start: e.target.value } : s))
                    }
                  />
                </div>
              </Col>
              <Col xs={6}>
                <div className="form-group">
                  <label>{intl.formatMessage({ id: 'endDate' })}</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={drawerForm.end}
                    onChange={(e) => setDrawerForm((s) => (s ? { ...s, end: e.target.value } : s))}
                  />
                </div>
              </Col>
            </Row>

            {drawerForm.interval === 'Weekly' && (
              <div className="form-group">
                <label>
                  {intl.formatMessage({ id: 'daysOfWeek', defaultMessage: 'Days of the week' })}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {WEEKDAYS.map((d) => {
                    const active = drawerForm.weeklySchedule.includes(String(d.value));
                    return (
                      <button
                        key={d.value}
                        type="button"
                        className={`button ${active ? 'primary' : ''}`}
                        onClick={() =>
                          setDrawerForm((s) =>
                            s
                              ? { ...s, weeklySchedule: toggleWeekday(s.weeklySchedule, d.value) }
                              : s
                          )
                        }
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {drawerForm.interval === 'Monthly' && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={drawerForm.monthlySameWeekday}
                    onChange={(e) =>
                      setDrawerForm((s) => (s ? { ...s, monthlySameWeekday: e.target.checked } : s))
                    }
                  />
                  <span>
                    {intl.formatMessage({
                      id: 'monthlySameWeekday',
                      defaultMessage: 'Repeat on the same weekday each month',
                    })}
                  </span>
                </label>
              </div>
            )}

            {drawerForm.interval === 'Hourly' && (
              <div className="form-group">
                <label>
                  {intl.formatMessage({ id: 'hoursOfDay', defaultMessage: 'Hours of the day' })}
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(8, 1fr)',
                    gap: '0.25rem',
                  }}
                >
                  {Array.from({ length: 24 }).map((_, hour) => {
                    const active = drawerForm.hourlySchedule.includes(hour);
                    return (
                      <button
                        key={hour}
                        type="button"
                        className={`button small ${active ? 'primary' : ''}`}
                        onClick={() =>
                          setDrawerForm((s) =>
                            s ? { ...s, hourlySchedule: toggleHour(s.hourlySchedule, hour) } : s
                          )
                        }
                      >
                        {String(hour).padStart(2, '0')}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {drawerForm.interval === 'CarCount' && (
              <Row>
                <Col xs={6}>
                  <div className="form-group">
                    <label>
                      {intl.formatMessage({
                        id: 'intervalCarCount',
                        defaultMessage: 'Cars per interval',
                      })}
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      value={drawerForm.intervalCarCount}
                      onChange={(e) =>
                        setDrawerForm((s) =>
                          s
                            ? {
                                ...s,
                                intervalCarCount:
                                  e.target.value === '' ? '' : Number(e.target.value),
                              }
                            : s
                        )
                      }
                    />
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="form-group">
                    <label>
                      {intl.formatMessage({
                        id: 'startingCarCount',
                        defaultMessage: 'Starting car count',
                      })}
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      value={drawerForm.startingCarCount}
                      onChange={(e) =>
                        setDrawerForm((s) =>
                          s
                            ? {
                                ...s,
                                startingCarCount:
                                  e.target.value === '' ? '' : Number(e.target.value),
                              }
                            : s
                        )
                      }
                    />
                  </div>
                </Col>
              </Row>
            )}

            <Row>
              <Col xs={6}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({
                      id: 'skipCompletionsNumber',
                      defaultMessage: 'Skip completions number',
                    })}
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="form-control"
                    value={drawerForm.skipCompletionsNumber}
                    onChange={(e) =>
                      setDrawerForm((s) =>
                        s
                          ? {
                              ...s,
                              skipCompletionsNumber:
                                e.target.value === '' ? '' : Number(e.target.value),
                            }
                          : s
                      )
                    }
                  />
                </div>
              </Col>
              <Col xs={6}>
                <div className="form-group" style={{ paddingTop: '1.6rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={drawerForm.alertIfOverdue}
                      onChange={(e) =>
                        setDrawerForm((s) => (s ? { ...s, alertIfOverdue: e.target.checked } : s))
                      }
                    />
                    <span>
                      {intl.formatMessage({
                        id: 'alertIfOverdue',
                        defaultMessage: 'Alert if overdue',
                      })}
                    </span>
                  </label>
                </div>
              </Col>
            </Row>
          </div>

          <div style={tabStyle('Defaults')}>
            {sectionHeader(intl.formatMessage({ id: 'defaults', defaultMessage: 'Defaults' }))}
            <Row>
              <Col xs={6}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({ id: 'teamMember', defaultMessage: 'Team Member' })}
                  </label>
                  <select
                    className="form-control"
                    value={drawerForm.employeeId === '' ? '' : String(drawerForm.employeeId)}
                    onChange={(e) =>
                      setDrawerForm((s) =>
                        s
                          ? {
                              ...s,
                              employeeId: e.target.value === '' ? '' : Number(e.target.value),
                            }
                          : s
                      )
                    }
                  >
                    <option value="">
                      {intl.formatMessage({ id: 'unassigned', defaultMessage: 'Unassigned' })}
                    </option>
                    {(employees || []).map((emp: any) => (
                      <option key={emp.id} value={emp.id}>
                        {`${emp.lastName}, ${emp.firstName}`}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col xs={6}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({ id: 'taskLocation', defaultMessage: 'Location' })}
                  </label>
                  <select
                    className="form-control"
                    value={drawerForm.locationId === '' ? '' : String(drawerForm.locationId)}
                    onChange={(e) =>
                      setDrawerForm((s) =>
                        s
                          ? {
                              ...s,
                              locationId: e.target.value === '' ? '' : Number(e.target.value),
                            }
                          : s
                      )
                    }
                  >
                    <option value="">
                      {intl.formatMessage({ id: 'none', defaultMessage: 'None' })}
                    </option>
                    {(locations || []).map((loc: any) => (
                      <option key={loc.type ?? loc.id} value={loc.type ?? loc.id}>
                        {loc.message ?? loc.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
            {drawerForm.type === 'Project' && (
              <Row>
                <Col xs={6}>
                  <div className="form-group">
                    <label>{intl.formatMessage({ id: 'prefix', defaultMessage: 'Prefix' })}</label>
                    <select
                      className="form-control"
                      value={drawerForm.prefixId === '' ? '' : String(drawerForm.prefixId)}
                      onChange={(e) =>
                        setDrawerForm((s) =>
                          s
                            ? {
                                ...s,
                                prefixId: e.target.value === '' ? '' : Number(e.target.value),
                              }
                            : s
                        )
                      }
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'none', defaultMessage: 'None' })}
                      </option>
                      {taskPrefixes.map((p) => (
                        <option key={p.type} value={p.type}>
                          {p.message}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={6}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({
                      id: 'operationTypeId',
                      defaultMessage: 'Operation type',
                    })}
                  </label>
                  <select
                    className="form-control"
                    value={
                      drawerForm.operationTypeId === '' ? '' : String(drawerForm.operationTypeId)
                    }
                    onChange={(e) =>
                      setDrawerForm((s) =>
                        s
                          ? {
                              ...s,
                              operationTypeId: e.target.value === '' ? '' : Number(e.target.value),
                            }
                          : s
                      )
                    }
                  >
                    <option value="">
                      {intl.formatMessage({ id: 'none', defaultMessage: 'None' })}
                    </option>
                    {(operationTypes || []).map((op: any) => (
                      <option key={op.id} value={op.id}>
                        {op.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col xs={3}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({ id: 'capacity', defaultMessage: 'Capacity' })}
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="form-control"
                    value={drawerForm.capacity}
                    onChange={(e) =>
                      setDrawerForm((s) =>
                        s
                          ? {
                              ...s,
                              capacity: e.target.value === '' ? '' : Number(e.target.value),
                            }
                          : s
                      )
                    }
                  />
                </div>
              </Col>
              <Col xs={3}>
                <div className="form-group">
                  <label>
                    {intl.formatMessage({
                      id: 'estimatedTime',
                      defaultMessage: 'Estimated (hr)',
                    })}
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    min={0}
                    className="form-control"
                    value={drawerForm.estimatedTime}
                    onChange={(e) =>
                      setDrawerForm((s) => (s ? { ...s, estimatedTime: e.target.value } : s))
                    }
                  />
                </div>
              </Col>
            </Row>
            {groupId != null && (
              <Row>
                <Col xs={12}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={drawerForm.applyToAllSitesInGroup}
                        onChange={(e) =>
                          setDrawerForm((s) =>
                            s ? { ...s, applyToAllSitesInGroup: e.target.checked } : s
                          )
                        }
                      />
                      <span>
                        {intl.formatMessage({
                          id: 'applyToAllSitesInGroup',
                          defaultMessage: 'Apply to all sites in this group (master template)',
                        })}
                      </span>
                    </label>
                  </div>
                </Col>
              </Row>
            )}
          </div>

          <div style={tabStyle('Media')}>
            {sectionHeader(intl.formatMessage({ id: 'resources', defaultMessage: 'Resources' }))}
            <div className="form-group">
              <label>
                {intl.formatMessage({ id: 'resource', defaultMessage: 'Resource URL' })}
              </label>
              <input
                type="url"
                className="form-control"
                placeholder="https://…"
                value={drawerForm.resource}
                onChange={(e) => setDrawerForm((s) => (s ? { ...s, resource: e.target.value } : s))}
              />
            </div>
            <div className="form-group">
              <label>
                {intl.formatMessage({
                  id: 'videos',
                  defaultMessage: 'Videos (one URL per line)',
                })}
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder={'https://example.com/video1\nhttps://example.com/video2'}
                value={drawerForm.videos}
                onChange={(e) => setDrawerForm((s) => (s ? { ...s, videos: e.target.value } : s))}
              />
            </div>
            <div className="form-group">
              <label>
                {intl.formatMessage({
                  id: 'uploadedMedia',
                  defaultMessage: 'Uploaded media',
                })}
              </label>
              <Files
                files={displayedMedia}
                addFileFunction={handleAddTemplateMedia}
                deleteFileFunction={handleDeleteTemplateMedia}
                canAdd={canModifyMedia}
                canDelete={canModifyMedia}
                multipleFiles
              />
              {mode === 'add' && pendingMedia.length > 0 && (
                <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                  {intl.formatMessage(
                    {
                      id: 'mediaStagedHint',
                      defaultMessage:
                        '{count} file(s) staged — they will upload when you click Save.',
                    },
                    { count: pendingMedia.length }
                  )}
                </p>
              )}
            </div>
          </div>

          <div style={tabStyle('Linked Tasks')}>
            {sectionHeader(
              intl.formatMessage({ id: 'linkedTasks', defaultMessage: 'Linked tasks' })
            )}
            <LinkedTaskTemplateField
              value={drawerForm.linkedTaskTemplates}
              onChange={(next) =>
                setDrawerForm((s) => (s ? { ...s, linkedTaskTemplates: next } : s))
              }
              excludeTaskId={drawerForm.id}
              disabled={isSaving}
            />
          </div>

          <div style={tabStyle('Item')}>
            {sectionHeader(intl.formatMessage({ id: 'item', defaultMessage: 'Item' }))}
            {assetScope &&
            (assetScope.assetGroupId != null ||
              assetScope.assetItemId != null ||
              assetScope.assetTemplateId != null) ? (
              <p style={{ color: '#374151', fontSize: '0.9rem', margin: 0 }}>
                {intl.formatMessage(
                  {
                    id: 'assets.drawer.taskScopedHint',
                    defaultMessage: 'This task will be scoped to the selected {kind} on save.',
                  },
                  {
                    kind:
                      assetScope.assetGroupId != null
                        ? 'group'
                        : assetScope.assetTemplateId != null
                        ? 'template'
                        : 'item',
                  }
                )}
              </p>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <UserGroupPermissions
              itemRef={mode === 'edit' ? editPermissionRefs.task : addPermissionRefs.task}
            >
              <button
                className="button primary"
                onClick={handleSaveDrawer}
                disabled={isSaving}
                type="button"
              >
                {isSaving
                  ? intl.formatMessage({ id: 'saving', defaultMessage: 'Saving…' })
                  : intl.formatMessage({ id: 'save', defaultMessage: 'Save' })}
              </button>
            </UserGroupPermissions>
            {mode === 'edit' && (
              <UserGroupPermissions itemRef={deletePermissionRefs.task}>
                <button
                  className="button"
                  onClick={handleArchive}
                  disabled={isSaving}
                  type="button"
                >
                  {intl.formatMessage({ id: 'archive', defaultMessage: 'Archive' })}
                </button>
              </UserGroupPermissions>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default TaskTemplateAdminDrawer;
